import { CardColors } from "$components/card";
import {
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/disease";
import {
  CardHeader,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import { useWeightAndAllergies } from "$routes/patient/_lib/use-weight-and-allergies";
import { styled, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { useRxData } from "rxdb-hooks";
import {
  type ChipOption,
  TextInputWithSelectableOptions,
} from "../../../_lib/text-input-with-selectable-options";
import { allVaccineCodesToDetailsMap } from "../../../_lib/vaccinations-service/vaccination-schedule";
import { getDiseaseCode } from "../../_lib/get-disease-code";

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: 20,
  marginTop: 20,
  paddingBottom: theme.spacing(3),
}));

export const AddVaccinationCard: React.FC<{
  patientId: string;
  fieldName: string;
  recommendedVaccinations: Array<{
    code: string;
  }>;
  vaccinations: Array<{ code: string }>;
  addVaccination: (vaccination: ChipOption) => void;
  removeVaccination: (index: number) => void;
  onDropdownOptionSelected?: (option: ChipOption) => void;
  onRecommendedVaccineSelected?: (option: ChipOption) => void;
  nudge?: React.ReactNode;
}> = ({
  patientId,
  fieldName,
  recommendedVaccinations,
  vaccinations,
  addVaccination,
  removeVaccination,
  onDropdownOptionSelected,
  onRecommendedVaccineSelected,
  nudge,
}) => {
  const { allergies } = useWeightAndAllergies({ patientId });
  const { result } = useRxData<Disease>(
    RXDB_DISEASE_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          $or: [
            { "catalogueCoding.icd11.code": { $regex: "^\\$IMM" } },
            { "catalogueCoding.icd10.code": { $regex: "^\\$IMM" } },
          ],
        },
      }),
  );
  const vaccinationDiseases = result.map((disease) => {
    const code = getDiseaseCode(disease);
    return {
      optionId: code,
      label: allVaccineCodesToDetailsMap[code].displayName,
      keywords: allVaccineCodesToDetailsMap[code].keywords,
    };
  });

  return (
    <PatientHistoryCard
      color={CardColors.PRESCRIPTION}
      expanded
      HeaderComponent={() => (
        <CardHeader color={CardColors.INVESTIGATION} title="Vaccinations" />
      )}
    >
      <Content>
        <Typography variant="body1">
          <FormattedMessage
            id="add-vaccination.allergies"
            defaultMessage="Allergies: {allergies}"
            values={{
              allergies: (
                <strong>
                  {allergies.length > 0
                    ? allergies.join(", ")
                    : "None recorded"}
                </strong>
              ),
            }}
          />
        </Typography>

        <TextInputWithSelectableOptions
          availableOptions={recommendedVaccinations.map(({ code }) => ({
            optionId: code,
            label: allVaccineCodesToDetailsMap[code].displayName,
          }))}
          chips={vaccinations.map(({ code }) => ({
            optionId: code,
            label: allVaccineCodesToDetailsMap[code].displayName,
          }))}
          addChip={addVaccination}
          removeChip={removeVaccination}
          chipsFieldName={fieldName}
          searchProps={{
            options: vaccinationDiseases,
            onDropdownOptionSelected,
          }}
          onRecommendedVaccineSelected={onRecommendedVaccineSelected}
          placeholder="Vaccination"
          nudge={nudge}
        />
      </Content>
    </PatientHistoryCard>
  );
};
